import { NotificationPayload } from 'firebase/messaging';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { match, P } from 'ts-pattern';

/**
 * Handle notification click.
 * Perform various actions based on the payload.
 *
 * @returns A callback function that handles notification click.
 */
export default function useHandleNotificationClick() {
  const navigate = useNavigate();

  return useCallback(
    (notification: NotificationPayload | undefined, data?: Record<string, unknown>) => {
      // FCM data values are always strings; also accept numeric ids coerced by senders.
      const normalized = normalizeNotificationData(data);
      match(normalized)
        .with({ orderId: P.string }, (matched) => {
          navigate({
            pathname: '/orders',
            search: new URLSearchParams({ orderId: matched.orderId }).toString(),
          });
        })
        .with({ complaintId: P.string }, (matched) => {
          navigate({
            pathname: '/user/complaints',
            search: new URLSearchParams({ complaintId: matched.complaintId }).toString(),
          });
        });
    },
    [navigate]
  );
}

function normalizeNotificationData(
  data?: Record<string, unknown>
): Record<string, unknown> | undefined {
  if (!data) return data;
  const normalized: Record<string, unknown> = { ...data };
  for (const key of ['orderId', 'complaintId'] as const) {
    const value = normalized[key];
    if (typeof value === 'number') normalized[key] = String(value);
  }
  return normalized;
}
